const V1_AUTH_PARAM_KEYS = ['ekid', 'enpd', 'enfd', 'cmac'];

const V2_AUTH_PARAM_KEYS = ['rid', 'p', 'c', 'm', 'original_url'];

const AUTH_PARAM_KEYS = [...V1_AUTH_PARAM_KEYS, ...V2_AUTH_PARAM_KEYS];
const CARD_TYPE_PARAM = 'type';

function getSearchParams(search = window.location.search) {
  const normalizedSearch = search.startsWith('?') ? search.slice(1) : search;

  return new URLSearchParams(normalizedSearch);
}

function getNonEmptyValue(searchParams, key) {
  const value = searchParams.get(key);

  if (typeof value !== 'string') {
    return null;
  }

  const trimmedValue = value.trim();

  return trimmedValue === '' ? null : trimmedValue;
}

export function getAuthParams(search = window.location.search) {
  const searchParams = getSearchParams(search);

  return {
    ekid: getNonEmptyValue(searchParams, 'ekid'),
    enpd: getNonEmptyValue(searchParams, 'enpd'),
    enfd: getNonEmptyValue(searchParams, 'enfd'),
    cmac: getNonEmptyValue(searchParams, 'cmac'),
    rid: getNonEmptyValue(searchParams, 'rid'),
    p: getNonEmptyValue(searchParams, 'p'),
    c: getNonEmptyValue(searchParams, 'c'),
    m: getNonEmptyValue(searchParams, 'm'),
    originalUrl: getNonEmptyValue(searchParams, 'original_url'),
    cardType: getNonEmptyValue(searchParams, CARD_TYPE_PARAM),
  };
}

function hasAllValues(values) {
  return values.every(
    (value) => typeof value === 'string' && value.trim() !== '',
  );
}

export function hasV1AuthParams({ ekid, enpd, enfd, cmac } = {}) {
  return hasAllValues([ekid, enpd, enfd, cmac]);
}

export function hasV2AuthParams({ rid, p, c, m, originalUrl } = {}) {
  return hasAllValues([rid, p, c, m, originalUrl]);
}

export function hasAnyAuthParams(authParams = {}) {
  return hasV1AuthParams(authParams) || hasV2AuthParams(authParams);
}

export function getAuthVersion(authParams = {}) {
  if (hasV1AuthParams(authParams)) {
    return 'v1';
  }

  if (hasV2AuthParams(authParams)) {
    return 'v2';
  }

  return null;
}

function removeParamsFromSearch(search, keys) {
  const searchParams = getSearchParams(search);

  keys.forEach((key) => searchParams.delete(key));

  const nextSearch = searchParams.toString();

  return nextSearch ? `?${nextSearch}` : '';
}

export function removeAuthParamsFromSearch(search = window.location.search) {
  return removeParamsFromSearch(search, AUTH_PARAM_KEYS);
}

export function removeCardTypeFromSearch(search = window.location.search) {
  return removeParamsFromSearch(search, [CARD_TYPE_PARAM]);
}

export function removeSensitiveParamsFromSearch(
  search = window.location.search,
) {
  return removeParamsFromSearch(search, [...AUTH_PARAM_KEYS, CARD_TYPE_PARAM]);
}

function replaceCurrentSearch(nextSearch) {
  const url = new URL(window.location.href);
  const nextUrl = `${url.pathname}${nextSearch}${url.hash}`;

  window.history.replaceState(window.history.state, '', nextUrl);
}

/**
 * Removes authentication credentials while preserving unrelated query
 * parameters, the card type, the pathname, and the hash.
 */
export function clearAuthParams() {
  replaceCurrentSearch(removeAuthParamsFromSearch());
}

/**
 * Removes only the card-type parameter while preserving all other parameters.
 */
export function clearCardTypeParam() {
  replaceCurrentSearch(removeCardTypeFromSearch());
}

/**
 * Removes credentials and card type before telemetry or after initialization.
 */
export function clearSensitiveParams() {
  replaceCurrentSearch(removeSensitiveParamsFromSearch());
}
